import {compact, noop} from '@benchling/js-utils';
import $ from 'jquery';
import * as PropTypes from 'prop-types';
import React from 'react';
import ReactDOM from 'react-dom';

import getTestProps from 'test/lib/cypress/getTestProps';
import ModalButtonKey from 'test/lib/cypress/shared/ModalButtonKey';
import {ButtonStyle} from 'ui-library/buttons/Button';
import Modal, {ModalButton, SharedModalProps} from 'ui-library/modals/Modal';
import {InputChangeEvent} from 'util/Types';

import * as ModalContainer from '../ui-library/ModalContainer';

const ALERT_COMPONENT_MODAL_KEY = 'AlertComponentModal';

interface AlertComponentProps extends SharedModalProps {
  onRequestClose: () => void;
  onRequestSubmit: () => void;
  text: React.ReactNode;
  style: 'default' | 'primary' | 'success' | 'danger' | ButtonStyle;
}

function AlertComponent(props: AlertComponentProps): JSX.Element {
  return (
    <Modal enableRedesign={false} {...props} buttons={[{text: 'OK', submit: true, style: props.style}]}>
      {props.text}
    </Modal>
  );
}

AlertComponent.propTypes = {
  text: PropTypes.node.isRequired,
  style: PropTypes.string.isRequired,
};

function alert(
  title: JSX.Element | string | null,
  text: React.ReactNode,
  type: 'default' | 'primary' | 'success' | 'danger' | ButtonStyle,
  otherProps: object = {}
): JQueryPromise<unknown> {
  if (type == null) {
    type = 'default';
  }
  const deferred = $.Deferred();
  ModalContainer.open(
    ALERT_COMPONENT_MODAL_KEY,
    <AlertComponent
      title={title}
      text={text}
      style={type}
      enableRedesign={false}
      onRequestSubmit={function (): void {
        ModalContainer.close(ALERT_COMPONENT_MODAL_KEY);
        deferred.resolve();
      }}
      onRequestClose={function (): void {
        ModalContainer.close(ALERT_COMPONENT_MODAL_KEY);
        deferred.reject();
      }}
      {...otherProps}
    />
  );
  return deferred.promise();
}

const CONFIRM_COMPONENT_MODAL_KEY = 'ConfirmComponentModal';
interface ConfirmComponentProps extends SharedModalProps {
  onRequestClose: () => void;
  onRequestSubmit: () => void;
  text: React.ReactNode;
  style: 'default' | 'primary' | 'success' | 'danger' | ButtonStyle;
  acceptText?: string;
  acceptDisabled?: boolean;
  rejectText?: string;
  onRequestCancel?: () => void;
}

export function ConfirmComponent(props: ConfirmComponentProps): JSX.Element {
  return (
    <Modal
      {...props}
      buttons={[
        {text: props.rejectText, cancel: true, onClick: props.onRequestCancel},
        {
          text: props.acceptText,
          submit: true,
          style: props.style,
          disabled: props.acceptDisabled,
          dataTestAttrs: {
            component: 'ConfirmComponent',
            element: 'modal',
            key: ModalButtonKey.ACCEPT,
          },
        },
      ]}
    >
      {props.text}
    </Modal>
  );
}

ConfirmComponent.propTypes = {
  text: PropTypes.node.isRequired,
  style: PropTypes.string.isRequired,
  acceptText: PropTypes.string.isRequired,
  acceptDisabled: PropTypes.bool.isRequired,
  rejectText: PropTypes.string.isRequired,
  onRequestCancel: PropTypes.func.isRequired,
};

ConfirmComponent.defaultProps = {
  acceptText: 'OK',
  rejectText: 'Cancel',
  acceptDisabled: false,
  onRequestCancel: noop,
};

function confirm(
  title: React.ReactNode,
  text: React.ReactNode,
  type: 'default' | 'primary' | 'success' | 'danger' | ButtonStyle = 'success',
  {
    acceptText,
    acceptDisabled,
    rejectText,
    promiseCompatible,
    enableRedesign,
    medium,
    onSubmit,
    onCancel,
    onExit,
  }: {
    acceptText?: string;
    acceptDisabled?: boolean;
    rejectText?: string;
    promiseCompatible?: boolean;
    enableRedesign?: boolean;
    medium?: boolean;
    onSubmit?: () => void;
    onCancel?: () => void;
    onExit?: () => void;
  } = {}
): JQueryPromise<boolean> {
  if (type == null) {
    type = 'success';
  }
  const deferred: JQueryDeferred<boolean> = $.Deferred();
  ModalContainer.open(
    CONFIRM_COMPONENT_MODAL_KEY,
    <ConfirmComponent
      title={title}
      text={text}
      style={type}
      enableRedesign={enableRedesign || false}
      acceptText={acceptText}
      acceptDisabled={acceptDisabled}
      rejectText={rejectText}
      onRequestCancel={function (): void {
        if (onCancel) {
          onCancel();
        }
        ModalContainer.close(CONFIRM_COMPONENT_MODAL_KEY);
        if (promiseCompatible) {
          deferred.resolve(false);
        } else {
          deferred.reject();
        }
      }}
      medium={medium}
      onRequestSubmit={function (): void {
        if (onSubmit) {
          onSubmit();
        }
        ModalContainer.close(CONFIRM_COMPONENT_MODAL_KEY);
        if (promiseCompatible) {
          deferred.resolve(true);
        } else {
          deferred.resolve();
        }
      }}
      onRequestClose={function (): void {
        if (onExit) {
          onExit();
        }
        ModalContainer.close(CONFIRM_COMPONENT_MODAL_KEY);
        if (promiseCompatible) {
          deferred.resolve(false);
        } else {
          deferred.reject();
        }
      }}
    />
  );
  return deferred.promise();
}

const MULTI_OPTION_MODAL_KEY = 'MultiOptionModal';
interface MultiOptionButton extends ModalButton {
  value: string;
}

interface MultiOptionComponentProps extends SharedModalProps {
  buttons: Array<MultiOptionButton>;
  text: string;
  onRequestClose: (value: string | null) => void;
}

class MultiOptionComponent extends React.Component<MultiOptionComponentProps> {
  static propTypes = {
    // Takes a value which will either be the value of the clicked button or undefined if the 'x' is
    // clicked.
    onRequestClose: PropTypes.func.isRequired,

    title: PropTypes.string.isRequired,
    text: PropTypes.string.isRequired,
    buttons: PropTypes.arrayOf(
      PropTypes.shape({
        text: PropTypes.string.isRequired,
        // eslint-disable-next-line react/forbid-prop-types
        value: PropTypes.any.isRequired,
        style: PropTypes.string,
      })
    ).isRequired,
  };

  onRequestClose = (value: string | null): void => {
    this.props.onRequestClose(value);
    ModalContainer.close(MULTI_OPTION_MODAL_KEY);
  };

  render(): JSX.Element {
    return (
      <Modal
        enableRedesign={false}
        {...this.props}
        onRequestClose={() => {
          this.onRequestClose(null);
        }}
        buttons={this.props.buttons.map((button) => ({
          onClick: () => {
            this.onRequestClose(button.value);
          },
          ...button,
        }))}
      >
        {this.props.text}
      </Modal>
    );
  }
}

// Returns a deferred which will be rejected if the user clicked the dialog's 'x'; otherwise will be resolved
// with the 'value' property of the clicked button.
function multiOption(
  title: JSX.Element | string | null,
  text: string,
  buttons: Array<MultiOptionButton>
): JQueryPromise<string> {
  const deferred: JQueryDeferred<string> = $.Deferred();
  ModalContainer.open(
    MULTI_OPTION_MODAL_KEY,
    <MultiOptionComponent
      title={title}
      text={text}
      buttons={buttons}
      enableRedesign={false}
      onRequestClose={function (value: string | null): void {
        if (value != null) {
          deferred.resolve(value);
        } else {
          deferred.reject();
        }
      }}
    />
  );
  return deferred;
}

const PROMPT_COMPONENT_MODAL_KEY = 'PromptComponentModal';

interface PromptComponentProps extends SharedModalProps {
  initialValue: string;
  onRequestSubmit: (value: string | null) => void;
  onRequestClose: () => void;
  validate: (value: string) => boolean;
  canReset?: boolean;
}

interface PromptComponentState {
  value: string;
}

class PromptComponent extends React.Component<PromptComponentProps, PromptComponentState> {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  static propTypes: any = {
    initialValue: PropTypes.string.isRequired,
    onRequestSubmit: PropTypes.func.isRequired,
    // Function from result string to a boolean that determined whether "OK" should be enabled.
    validate: PropTypes.func.isRequired,
    canReset: PropTypes.bool,
  };

  state: PromptComponentState = {
    value: this.props.initialValue,
  };

  inputRef = React.createRef<HTMLInputElement>();

  onRequestSubmit = (): void => {
    this.props.onRequestSubmit(this.state.value);
  };

  componentDidMount(): void {
    $(ReactDOM.findDOMNode(this.inputRef.current)!).focus().select();
  }

  handleChange = (event: InputChangeEvent): void => {
    this.setState({value: event.target.value});
  };

  render(): JSX.Element {
    return (
      <Modal
        enableRedesign={true}
        {...this.props}
        onRequestSubmit={this.onRequestSubmit}
        buttons={compact([
          {text: 'Cancel', cancel: true, style: ButtonStyle.TERTIARY_GRAY},
          {
            text: 'OK',
            submit: true,
            style: ButtonStyle.PRIMARY,
            disabled: !this.props.validate(this.state.value),
            dataTestAttrs: {
              component: 'PromptComponent',
              element: 'modal',
              key: 'okButton',
            },
          },
          this.props.canReset
            ? {
                text: 'Reset',
                onClick: () => {
                  this.props.onRequestSubmit(null);
                },
                style: ButtonStyle.NEGATIVE,
                pullLeft: true,
              }
            : undefined,
        ])}
        dataTestAttrs={{component: 'PromptComponent', element: 'modal'}}
      >
        <input
          type="text"
          value={this.state.value}
          onChange={this.handleChange}
          ref={this.inputRef}
          {...getTestProps({component: 'PromptComponent', element: 'input'})}
        />
      </Modal>
    );
  }
}

function prompt(
  title: JSX.Element | string | null,
  initialValue?: string | null,
  validate?: (value: string) => boolean,
  param?: {canReset?: boolean}
): JQueryPromise<string> {
  if (initialValue == null) {
    initialValue = '';
  }
  if (validate == null) {
    validate = () => true;
  }
  if (param == null) {
    param = {};
  }
  const {canReset} = param;
  const deferred: JQueryDeferred<string> = $.Deferred();
  ModalContainer.open(
    PROMPT_COMPONENT_MODAL_KEY,
    <PromptComponent
      title={title}
      initialValue={initialValue}
      validate={validate}
      canReset={canReset}
      onRequestSubmit={function (value: string): void {
        ModalContainer.close(PROMPT_COMPONENT_MODAL_KEY);
        deferred.resolve(value);
      }}
      onRequestClose={function (): void {
        ModalContainer.close(PROMPT_COMPONENT_MODAL_KEY);
        deferred.reject();
      }}
    />
  );
  return deferred.promise();
}

export {alert, confirm, prompt, multiOption, PromptComponent as PromptModal};
